import type { SheetData, DatasetShape } from './types';

const HEADER_KEYWORDS = /timestamp|date|time|dttm|kw\b|kwh|demand|usage|power|value|kva/i;

/**
 * Whether a cell reads as a header keyword.
 *
 * A typed date cell never does. Its string form is "Tue Oct 28 2025 00:15:00
 * GMT+0000 (Coordinated Universal Time)", which contains "time", so a workbook
 * whose real header carried no keyword (Eversource: ServiceAgreement,
 * IntervalStart, IntervalEnd, Quantity, UnitOfMeasure) had its first data row
 * taken as the header — two date cells and a "kWh" made three matches.
 */
function isHeaderKeyword(cell: unknown): boolean {
  if (cell === null || cell === undefined || cell instanceof Date) return false;
  return HEADER_KEYWORDS.test(String(cell));
}

// Anchored so it matches an identifier column ("Meter Number", "Meter", "Service
// Location", "ESIID") and not a measurement column that merely mentions a meter
// ("Meter Reading", "Meter Multiplier"), which would misread a value column as
// an identity and split a perfectly good single-meter file.
const METER_ID_HEADER = /^(meter(\s*(number|num|no\.?|id))?|service\s*(point|location)|esiid|point\s*id)$/i;

// Stop counting distinct meters past this many. The count only drives a refusal
// message, so an exact tally of a pathological file is worth nothing, while
// scanning every row of a 50k-row export to build a huge Set is a real cost.
const MAX_TRACKED_METERS = 24;

export function classifyDataset(sheets: SheetData[]): DatasetShape {
  if (sheets.length === 0) {
    return { kind: 'single-meter', sheet: '', headerRowIndex: 0, dataStartRow: 1, notes: ['No sheets provided'] };
  }

  // ── Sheet selection ───────────────────────────────────────────────────────
  let bestSheet = sheets[0];
  let bestScore = -1;
  // Observational only: record each sheet's header-keyword density + row count.
  // Selection below is byte-for-byte unchanged; this array is never read here.
  const sheetScores: Array<{ name: string; score: number; rowCount: number }> = [];

  for (const sheet of sheets) {
    let maxRowScore = 0;
    for (let r = 0; r < Math.min(30, sheet.rows.length); r++) {
      const row = sheet.rows[r] as unknown[];
      const rowScore = row.filter(isHeaderKeyword).length;
      if (rowScore > maxRowScore) maxRowScore = rowScore;
    }
    sheetScores.push({ name: sheet.name, score: maxRowScore, rowCount: sheet.rows.length });
    if (maxRowScore > bestScore || (maxRowScore === bestScore && sheet.rows.length > bestSheet.rows.length)) {
      bestScore = maxRowScore;
      bestSheet = sheet;
    }
  }

  // ── Header row detection ──────────────────────────────────────────────────
  let headerRowIndex = 0;
  // Observational only: true when a real header row was matched below, false
  // when we fall through to the default index 0. Does not gate any behavior.
  let headerConfident = false;
  const rows = bestSheet.rows;

  for (let r = 0; r < Math.min(30, rows.length); r++) {
    const row = rows[r] as unknown[];
    const nonNull = row.filter(c => c !== null && c !== undefined && String(c).trim() !== '');
    // Title row: exactly 1 non-null cell — header is next row,
    // but only if the next row actually looks like a header row.
    // Metadata preambles (e.g. GMP meter+weather exports) contain
    // single-cell text lines that must not trigger this rule.
    if (nonNull.length === 1 && r + 1 < rows.length) {
      const nextRow = rows[r + 1] as unknown[];
      const nextMatches = nextRow.filter(isHeaderKeyword).length;
      if (nextMatches >= 2) {
        headerRowIndex = r + 1;
        headerConfident = true;
        break;
      }
      continue;
    }
    // Header row: ≥2 keyword matches
    const matches = row.filter(isHeaderKeyword).length;
    if (matches >= 2) {
      headerRowIndex = r;
      headerConfident = true;
      break;
    }
  }

  const headerRow0 = (rows[headerRowIndex] ?? []) as unknown[];

  // ── Multi-meter detection ─────────────────────────────────────────────────
  // A meter-identifier column holding more than one distinct value means the
  // sheet interleaves readings from separate meters. Left undetected they merge
  // into one series: every timestamp appears once per meter, and the downstream
  // 'maximum' conflict strategy resolves each collision to whichever meter read
  // higher. That silently reports the envelope of several services as one
  // panel's demand, and misstating existing load is exactly what wrongly frees
  // or denies panel capacity.
  //
  // Keyed on the meter column, deliberately NOT on duplicate timestamps: DST
  // fall-back repeats an hour, so nearly every committed fixture carries a few
  // duplicate timestamps legitimately. Measured across all 13 fixtures, this
  // predicate fires on exactly one — the VEC net-metering export, with two
  // meters and 8,760 duplicated timestamps. GMP's dual-channel solar export is
  // dual-*column*, not interleaved rows, and is untouched by this.
  const meterIdColumn = headerRow0.findIndex(
    c => c !== null && c !== undefined && METER_ID_HEADER.test(String(c).trim())
  );
  if (meterIdColumn >= 0) {
    const meters = new Set<string>();
    for (let r = headerRowIndex + 1; r < rows.length; r++) {
      const raw = (rows[r] as unknown[])[meterIdColumn];
      if (raw === null || raw === undefined) continue;
      const v = String(raw).trim();
      if (v === '') continue;
      meters.add(v);
      if (meters.size > MAX_TRACKED_METERS) break;
    }
    if (meters.size > 1) {
      return {
        kind: 'multi-meter-interleaved',
        sheet: bestSheet.name,
        headerRowIndex,
        dataStartRow: headerRowIndex + 1,
        headerConfident,
        sheetScores,
        meterIdColumn,
        meterIdValues: [...meters],
        notes: [
          `Selected sheet "${bestSheet.name}" with ${bestScore} header matches`,
          `Header detected at row ${headerRowIndex}`,
          `Multiple meters detected in column ${meterIdColumn} ("${String(headerRow0[meterIdColumn]).trim()}")`,
        ],
      };
    }
  }

  // TODO Batch 2+: detect multi-sheet-meters, daily-peak-register
  return {
    kind: 'single-meter',
    sheet: bestSheet.name,
    headerRowIndex,
    dataStartRow: headerRowIndex + 1,
    headerConfident,
    sheetScores,
    notes: [
      `Selected sheet "${bestSheet.name}" with ${bestScore} header matches`,
      `Header detected at row ${headerRowIndex}`,
    ],
  };
}
